import { GestureType } from '../types/planet';
import type { GestureEvent } from '../types/planet';

/**
 * 手势识别引擎 v3 — 防误触设计
 *
 * 核心原则：宁可漏识别，不可误触发
 *   - 所有手势需连续 5 帧确认（~167ms at 30fps）
 *   - 每个手势条件严格互斥、高度特异
 *   - 冷却时间充足（同手势 3s，不同手势 1s）
 *
 * 调试面板格式：
 *  右手: 张开(1.00) [T0 I1 M1 R1 P1] SW=0.02/5
 *  T=拇指 I=食指 M=中指 R=无名指 P=小指  1=伸 0=弯
 */
export class GestureEngine {
  private prevLandmarks: Array<{ x: number; y: number; z: number }[]> = [];
  private onGesture: (event: GestureEvent) => void;

  // ---- 阈值 ----
  private readonly EXTEND_THRESHOLD = 0.02;  // Y 方向伸直阈值

  // ---- 调试 ----
  public onDebugInfo: ((info: string) => void) | null = null;

  // ---- 状态机 ----
  private lastTriggerTimes: Map<GestureType, number> = new Map();
  private readonly COOLDOWN_SAME = 1500;  // 同手势 1.5 秒
  private readonly COOLDOWN_DIFF = 400;   // 不同手势 0.4 秒

  private confirmBuf: Map<string, GestureType[]> = new Map();
  private readonly CONFIRM_FRAMES = 2;     // 连续 2 帧确认

  private deactCooldown: Map<string, number> = new Map();
  private readonly DEACT_FRAMES = 2;       // 去激活等 2 帧

  private activeType: Map<string, GestureType> = new Map();

  constructor(onGesture: (event: GestureEvent) => void) {
    this.onGesture = onGesture;
  }

  public analyze(landmarksList: Array<{ x: number; y: number; z: number }[]>) {
    const now = Date.now();
    let dbg = `👋 ${landmarksList.length}手`;

    // 清理已离开的手
    const cur = landmarksList.map((_, i) => i === 0 ? 'right' as const : 'left' as const);
    for (const h of this.confirmBuf.keys()) {
      if (!cur.includes(h as any)) {
        this.confirmBuf.delete(h); this.activeType.delete(h);
        this.deactCooldown.delete(h);
      }
    }

    for (let i = 0; i < landmarksList.length; i++) {
      const lm = landmarksList[i];
      const label = i === 0 ? '右手' : '左手';
      const key: 'right' | 'left' = i === 0 ? 'right' : 'left';

      // 手指状态
      const T = + (lm[4].y < lm[2].y - this.EXTEND_THRESHOLD);
      const I = + (lm[8].y < lm[5].y - this.EXTEND_THRESHOLD);
      const M = + (lm[12].y < lm[9].y - this.EXTEND_THRESHOLD);
      const R = + (lm[16].y < lm[13].y - this.EXTEND_THRESHOLD);
      const P = + (lm[20].y < lm[17].y - this.EXTEND_THRESHOLD);

      const g = this.classify(T, I, M, R, P, lm);
      dbg += `\n  ${label}: ${this.gname(g.type)}(${g.confidence.toFixed(2)}) [T${T} I${I} M${M} R${R} P${P}] dy=${(lm[2].y - lm[4].y).toFixed(3)}`;

      this.pushBuf(key, g.type);

      if (g.type === GestureType.NONE) {
        if (this.activeType.get(key) !== undefined && this.activeType.get(key) !== GestureType.NONE) {
          this.deactCooldown.set(key, this.DEACT_FRAMES);
        }
        this.activeType.set(key, GestureType.NONE);
        continue;
      }

      const dc = this.deactCooldown.get(key) || 0;
      if (dc > 0) { this.deactCooldown.set(key, dc - 1); continue; }
      if (!this.confirmed(key, g.type)) continue;

      const sameCd = this.lastTriggerTimes.get(g.type) || 0;
      if (now - sameCd < this.COOLDOWN_SAME) continue;

      let anyRecent = false;
      for (const [, t] of this.lastTriggerTimes) {
        if (now - t < this.COOLDOWN_DIFF) { anyRecent = true; break; }
      }
      if (anyRecent) continue;

      if (this.activeType.get(key) === g.type) continue;

      this.lastTriggerTimes.set(g.type, now);
      this.activeType.set(key, g.type);

      this.onGesture({
        type: g.type,
        hand: key,
        confidence: g.confidence,
        handPosition: { x: lm[8].x, y: lm[8].y },
        data: { landmarks: lm, index: i },
      });
    }

    if (this.onDebugInfo) this.onDebugInfo(dbg);
    if (landmarksList.length === 2) this.tryTwoHands(landmarksList);
    this.prevLandmarks = landmarksList.map(l => l.map(p => ({ x: p.x, y: p.y, z: p.z })));
  }

  // ====== 手势分类（互斥优先级） ======
  private classify(
    T: number, I: number, M: number, R: number, P: number,
    lm: { x: number; y: number; z: number }[]
  ): { type: GestureType; confidence: number } {

    // 1. 五指张开：4非拇指伸直 + 手指分开（食指↔小指距离 > 0.15）
    if (I && M && R && P) {
      const spread = this.dist2(lm[8], lm[20]);
      if (spread > 0.15) {
        return { type: GestureType.OPEN_PALM, confidence: 1.0 };
      }
    }

    // 2. 剪刀手：食指+中指伸直，无名指+小指弯曲
    if (I && M && !R && !P) {
      return { type: GestureType.SCISSORS, confidence: 1.0 };
    }
    const ext = T + I + M + R + P;
    if (I && M && ext <= 3) {
      return { type: GestureType.SCISSORS, confidence: 0.7 + (3 - ext) * 0.1 };
    }

    // 3. 食指指向：仅食指伸直，其余全弯 → 时间加减
    //    注意：前置摄像头镜像后，用户伸出的"右手食指"出现在画面左侧
    //    用户指向自己右侧 → 画面中食指指尖偏左 → ix < 0 → SWIPE_RIGHT（加速）
    if (I && !T && !M && !R && !P) {
      const ix = lm[8].x - lm[5].x; // 食指尖X - MCP X
      if (ix < -0.03) return { type: GestureType.SWIPE_RIGHT, confidence: Math.min(1, 0.4 + Math.abs(ix) * 8) };
      if (ix > 0.03) return { type: GestureType.SWIPE_LEFT, confidence: Math.min(1, 0.4 + ix * 8) };
    }

    // 4. 大拇指朝上/朝下：仅拇指伸直（用3D距离判断），其余全弯
    const thumbDist3D = Math.hypot(lm[4].x - lm[2].x, lm[4].y - lm[2].y, lm[4].z - lm[2].z);
    const thumbReallyExtended = thumbDist3D > 0.10;
    if (thumbReallyExtended && !I && !M && !R && !P) {
      const dy = lm[2].y - lm[4].y;
      if (dy > 0.03) return { type: GestureType.THUMB_UP, confidence: Math.min(1, 0.5 + dy * 5) };
      if (dy < -0.03) return { type: GestureType.THUMB_DOWN, confidence: Math.min(1, 0.5 + Math.abs(dy) * 5) };
    }

    return { type: GestureType.NONE, confidence: 0 };
  }

  // ====== 双手缩放 ======
  private tryTwoHands(landmarksList: Array<{ x: number; y: number; z: number }[]>) {
    if (this.prevLandmarks.length !== 2) return;
    const now = Date.now();
    const last = this.lastTriggerTimes.get(GestureType.TWO_HAND_PINCH) || 0;
    if (now - last < this.COOLDOWN_SAME) return;
    for (const [, t] of this.lastTriggerTimes) {
      if (now - t < this.COOLDOWN_DIFF) return;
    }

    const a = this.handCtr(landmarksList[0]), b = this.handCtr(landmarksList[1]);
    const pa = this.handCtr(this.prevLandmarks[0]), pb = this.handCtr(this.prevLandmarks[1]);
    const delta = this.dist2(a, b) - this.dist2(pa, pb);
    if (Math.abs(delta) > 0.03) {
      this.lastTriggerTimes.set(GestureType.TWO_HAND_PINCH, now);
      this.onGesture({ type: GestureType.TWO_HAND_PINCH, hand: 'both',
        confidence: Math.min(Math.abs(delta) * 20, 1),
        data: { delta, cur: this.dist2(a, b), prev: this.dist2(pa, pb) } });
    }
  }

  // ====== 工具 ======
  private pushBuf(hand: string, type: GestureType) {
    if (!this.confirmBuf.has(hand)) this.confirmBuf.set(hand, []);
    const b = this.confirmBuf.get(hand)!; b.push(type);
    if (b.length > this.CONFIRM_FRAMES) this.confirmBuf.set(hand, b.slice(-this.CONFIRM_FRAMES));
  }
  private confirmed(hand: string, type: GestureType) {
    const b = this.confirmBuf.get(hand);
    return b && b.length >= this.CONFIRM_FRAMES && b.every(g => g === type);
  }
  private gname(t: GestureType): string {
    return ({ [GestureType.NONE]: '无', [GestureType.SCISSORS]: '剪刀手',
      [GestureType.OPEN_PALM]: '张开', [GestureType.SWIPE_LEFT]: '左指',
      [GestureType.SWIPE_RIGHT]: '右指', [GestureType.TWO_HAND_PINCH]: '双手',
      [GestureType.THUMB_UP]: '上拇', [GestureType.THUMB_DOWN]: '下拇' } as any)[t] || '?';
  }
  private dist2(a: { x: number; y: number }, b: { x: number; y: number }) {
    return Math.hypot(a.x - b.x, a.y - b.y);
  }
  private handCtr(lm: { x: number; y: number; z: number }[]) {
    return { x: (lm[0].x + lm[9].x) / 2, y: (lm[0].y + lm[9].y) / 2 };
  }
}
